import { createLogPrefix, logDebug, type LogTarget, noop } from '@amazon/vinyl-util'
import { noopPlaybackPerf } from './noopPlaybackPerf'
import type { PlaybackMeasureToken, PlaybackPerf } from './PlaybackEnv'

/** The interaction-to-playing half of {@link PlaybackPerf}. */
export type PlaybackDelayMeasure = Pick<
    PlaybackPerf,
    | 'markInteractionStart'
    | 'clearInteractionStart'
    | 'markPlayStart'
    | 'currentMeasureToken'
    | 'markPlaying'
    | 'peekPlaybackDelayMs'
    | 'cancelPlaybackMeasure'
    | 'interactionAgeSeconds'
>

/** A play's interaction-to-playing span, on the measure's clock. */
export interface MeasuredPlaybackDelay {
    readonly start: number
    readonly end: number
    readonly currentTrackTitle: string | undefined
}

// Drop a leaked window this old so a much-later machine `playing` (autoplay re-seed) can't close
// it with an over-long span.
const MAX_WINDOW_AGE_MS = 60_000

// A play's terminal metric reads its delay shortly after `playing`, so a few recent plays suffice.
const MAX_RETAINED_MEASURES = 8

const logTarget: LogTarget = { logPrefix: createLogPrefix('ipd') }

/**
 * Correlates one initial-playback attempt at a time: a new-track interaction (or the queue sink)
 * opens the window, the first `playing` closes it. Each open mints an owner token, and only the
 * owning play may close or cancel it, so an outgoing play's teardown can't wipe a newer play's
 * window. `now` is in ms; `onMeasured` draws each closed window.
 */
export function createPlaybackDelayMeasure(
    now: () => number,
    onMeasured: (delay: MeasuredPlaybackDelay) => void = noop
): PlaybackDelayMeasure {
    let seq = 0
    let startedAt: number | null = null
    let owner: PlaybackMeasureToken = 0
    const measuredDelayByToken = new Map<PlaybackMeasureToken, number>()

    // Debug-gated trace at every window mutation, to pinpoint an intermittent miss.
    const trace = (event: string, detail: Record<string, unknown>) =>
        logDebug(logTarget, `[ipd] ${event}`, { seq, startedAt, owner, ...detail })

    const openWindow = (reason: string, ageSeconds = 0) => {
        const before = startedAt
        startedAt = now() - (Number.isFinite(ageSeconds) && ageSeconds > 0 ? ageSeconds * 1000 : 0)
        owner = ++seq
        trace(reason, { before, ageSeconds })
        return owner
    }

    const isStale = (token: PlaybackMeasureToken | undefined) => token != null && token !== owner

    return {
        markInteractionStart: (ageSeconds) => openWindow('interactionStart', ageSeconds),

        clearInteractionStart(token) {
            if (isStale(token)) {
                trace('clear-skip-stale', { token })
                return
            }
            startedAt = null
            trace('clear', { token })
        },

        markPlayStart(ageSeconds) {
            if (startedAt == null) return openWindow('playStart', ageSeconds)
            trace('playStart-keep', {})
            return owner
        },

        currentMeasureToken: () => owner,

        markPlaying(currentTrackTitle, token) {
            if (isStale(token)) {
                trace('playing-skip-stale', { token, title: currentTrackTitle })
                return
            }
            const start = startedAt
            startedAt = null
            if (start == null) {
                trace('playing-no-window', { title: currentTrackTitle })
                return
            }
            const end = now()
            if (end - start > MAX_WINDOW_AGE_MS) {
                trace('playing-stale-age', { title: currentTrackTitle, start })
                return
            }
            // Clamp against a backward wall-clock step (NTP) so the span never reverses.
            const clampedEnd = Math.max(start, end)
            measuredDelayByToken.set(owner, clampedEnd - start)
            if (measuredDelayByToken.size > MAX_RETAINED_MEASURES) {
                measuredDelayByToken.delete(measuredDelayByToken.keys().next().value!)
            }
            trace('playing-draw', { title: currentTrackTitle, start, end: clampedEnd })
            onMeasured({ start, end: clampedEnd, currentTrackTitle })
        },

        peekPlaybackDelayMs: (token) =>
            token == null ? null : (measuredDelayByToken.get(token) ?? null),

        cancelPlaybackMeasure(reason, token) {
            if (isStale(token)) {
                trace('cancel-skip-stale', { reason, token })
                return
            }
            const before = startedAt
            startedAt = null
            trace('cancel', { reason, before })
        },

        interactionAgeSeconds() {
            if (startedAt == null) return null
            const ageMs = now() - startedAt
            return ageMs > MAX_WINDOW_AGE_MS ? null : Math.max(0, ageMs) / 1000
        },
    }
}

/**
 * For a host with no timeline: measures each play's interaction-to-playing delay, which the
 * playback metrics upload, and draws nothing.
 */
export function measuringPlaybackPerf(now: () => number = Date.now): PlaybackPerf {
    return { ...noopPlaybackPerf, ...createPlaybackDelayMeasure(now) }
}
